import { NgModule, isDevMode } from '@angular/core';
import { HashLocationStrategy, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReactiveFormsModule } from '@angular/forms';

import { NgScrollbarModule } from 'ngx-scrollbar';

// Import routing module
import { AppRoutingModule } from './app-routing.module';

// Import app component
import { AppComponent } from './app.component';



// Import containers
import {
  DefaultAsideComponent,
  DefaultFooterComponent,
  DefaultHeaderComponent,
  DefaultLayoutComponent,
  EmailLayoutComponent
} from './containers';

import {
  AvatarModule,
  BadgeModule,
  BreadcrumbModule,
  ButtonGroupModule,
  ButtonModule,
  CardModule,
  DropdownModule,
  FooterModule,
  FormModule,
  GridModule,
  HeaderModule,
  ListGroupModule,
  NavModule,
  ProgressModule,
  SharedModule,
  SidebarModule,
  ModalModule,
  TabsModule,
  UtilitiesModule,
  TableModule,
} from '@coreui/angular-pro';

import { IconModule, IconSetService } from '@coreui/icons-angular';
import { HttpClientModule } from '@angular/common/http';
import { TranslocoRootModule } from './transloco-root.module';

import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { ColorPickerModule } from 'ngx-color-picker';
import { CountdownModule } from 'ngx-countdown';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { FileUploadModule } from 'ng2-file-upload';
import { NgxSliderModule } from 'ngx-slider-v2';

import { YesNoPipe } from './pipes/yes-no.pipe';
import { FieldtitlePipe } from './pipes/fieldtitle.pipe';
import { Nl2brPipe } from './pipes/nl2br.pipe';
import { UtcToTimezonePipe } from './pipes/utc-to-timezone.pipe';

import { FullCalendarModule } from '@fullcalendar/angular';


import { SettingsComponent } from './pages/settings/settings/settings.component';
import { WorkersComponent } from './pages/mgmt/workers/workers/workers.component';
import { WorkerAddComponent } from './pages/mgmt/workers/worker-add/worker-add.component';
import { WorkerEditComponent } from './pages/mgmt/workers/worker-edit/worker-edit.component';
import { MonitorsComponent } from './pages/mgmt/monitors/monitors/monitors.component';
import { MonitorsPrivacyComponent } from './pages/mgmt/monitors/monitors-privacy/monitors-privacy.component';
import { MonitorPrivacyEditComponent } from './pages/mgmt/monitors/monitor-privacy-edit/monitor-privacy-edit.component';
import { MonitorAddComponent } from './pages/mgmt/monitors/monitor-add/monitor-add.component';
import { MonitorEditComponent } from './pages/mgmt/monitors/monitor-edit/monitor-edit.component';
import { MonitorsUploadComponent } from './pages/mgmt/monitors/monitors-upload/monitors-upload.component';
import { VehiclesComponent } from './pages/mgmt/vehicles/vehicles.component';
import { DevicesComponent } from './pages/mgmt/devices/devices.component';
import { EscalationComponent } from './pages/settings/escalation/escalation.component';
import { EscalationEditCustomComponent } from './pages/settings/escalation/escalation-edit-custom/escalation-edit-custom.component';
import { EscalationEditWizardComponent } from './pages/settings/escalation/escalation-edit-wizard/escalation-edit-wizard.component';
import { ShiftsComponent } from './pages/mgmt/shifts/shifts/shifts.component';
import { ShiftComponent } from './pages/mgmt/shifts/shift/shift.component';
import { AlertsComponent } from './pages/reports/alerts/alerts/alerts.component';
import { AlertPageComponent } from './pages/reports/alerts/alert-page/alert-page.component';
import { ActionsComponent } from './pages/reports/actions/actions.component';
import { LocationsComponent } from './pages/reports/locations/locations.component';
import { OffenderListComponent } from './pages/reports/locations/offender-list/offender-list.component';
import { WeatherAlertsComponent } from './pages/reports/locations/weather-alerts/weather-alerts.component';
import { TimesheetsComponent } from './pages/reports/timesheets/timesheets.component';
import { ProfileComponent } from './pages/settings/profile/profile.component';
import { HelpComponent } from './pages/settings/help/help.component';
import { ChooseAppComponent } from './pages/settings/choose-app/choose-app.component';
import { WorkersImportComponent } from './pages/mgmt/workers/workers-import/workers-import.component';
import { MonitorsImportComponent } from './pages/mgmt/monitors/monitors-import/monitors-import.component';
import { AlertloopsComponent } from './pages/mgmt/alertloops/alertloops/alertloops.component';
import { AlertloopEditComponent } from './pages/mgmt/alertloops/alertloop-edit/alertloop-edit.component';
import { AlertloopsUploadComponent } from './pages/mgmt/alertloops/alertloops-upload/alertloops-upload.component';
import { GroupsComponent } from './pages/mgmt/groups/groups/groups.component';
import { GroupEditComponent } from './pages/mgmt/groups/group-edit/group-edit.component';
import { GroupEscalationComponent } from './pages/mgmt/groups/group-escalation/group-escalation.component';
import { GroupsUploadComponent } from './pages/mgmt/groups/groups-upload/groups-upload.component';
import { OutofhoursComponent } from './pages/mgmt/outofhours/outofhours.component';
import { StartEndComponent } from './pages/mgmt/shifts/start-end/start-end.component';
import { W3wComponent } from './pages/settings/w3w/w3w.component';
import { ZapComponent } from './pages/settings/zap/zap.component';
import { LiveMonitorComponent } from './pages/settings/live-monitor/live-monitor.component';
import { AwardsHomeComponent } from './pages/awards/awards-home/awards-home.component';
import { AwardsNavComponent } from './pages/awards/awards-nav/awards-nav.component';
import { AwardsMainComponent } from './pages/awards/awards-main/awards-main.component';
import { AwardsExamplesComponent } from './pages/awards/awards-examples/awards-examples.component';
import { AwardsFaqComponent } from './pages/awards/awards-faq/awards-faq.component';
import { ChampionsComponent } from './pages/awards/champions/champions.component';
import { CertificatesComponent } from './pages/awards/certificates/certificates.component';
import { ReportcardsComponent } from './pages/awards/reportcards/reportcards.component';
import { CompanyrankingsComponent } from './pages/awards/companyrankings/companyrankings.component';
import { WorkerrankingsComponent } from './pages/awards/workerrankings/workerrankings.component';
import { AwardsDemoComponent } from './pages/awards/awards-demo/awards-demo.component';
import { WorkersUploadComponent } from './pages/mgmt/workers/workers-upload/workers-upload.component';

import { WorkerInfoComponent } from './pages/mgmt/workers/worker-edit/tabs/worker-info/worker-info.component';
import { WorkerCheckinComponent } from './pages/mgmt/workers/worker-edit/tabs/worker-checkin/worker-checkin.component';
import { WorkerSafetyzoneComponent } from './pages/mgmt/workers/worker-edit/tabs/worker-safetyzone/worker-safetyzone.component';
import { WorkerEscalationComponent } from './pages/mgmt/workers/worker-edit/tabs/worker-escalation/worker-escalation.component';
import { WorkerShiftComponent } from './pages/mgmt/workers/worker-edit/tabs/worker-shift/worker-shift.component';
import { WorkerAppComponent } from './pages/mgmt/workers/worker-edit/tabs/worker-app/worker-app.component';
import { WorkerPermissionsComponent } from './pages/mgmt/workers/worker-edit/tabs/worker-permissions/worker-permissions.component';
import { WorkerSpotComponent } from './pages/mgmt/workers/worker-edit/tabs/worker-spot/worker-spot.component';
import { WorkersAdpComponent } from './pages/mgmt/workers/workers-adp/workers-adp.component';
import { WorkersMassdeleteComponent } from './pages/mgmt/workers/workers-massdelete/workers-massdelete.component';
import { MonitorsAdpComponent } from './pages/mgmt/monitors/monitors-adp/monitors-adp.component';
import { ImageUploadComponent } from './pages/mgmt/workers/worker-edit/image-upload/image-upload.component';
import { MessagesComponent } from './pages/mgmt/workers/messages/messages.component';
import { WorkerIhelpComponent } from './pages/mgmt/workers/worker-edit/tabs/worker-ihelp/worker-ihelp.component';
import { WorkerZoleoComponent } from './pages/mgmt/workers/worker-edit/tabs/worker-zoleo/worker-zoleo.component';
import { WorkersInactiveComponent } from './pages/mgmt/workers/workers-inactive/workers-inactive.component';

import { WorkerReportsComponent } from './pages/reports/worker-reports/worker-reports.component';
import { WorkerStatsComponent } from './pages/reports/worker-reports/worker-stats/worker-stats.component';
import { WorkerStatusComponent } from './pages/reports/worker-reports/worker-status/worker-status.component';
import { WorkerAppNotActivatedComponent } from './pages/reports/worker-reports/worker-app-not-activated/worker-app-not-activated.component';
import { WorkerHasStartedShiftComponent } from './pages/reports/worker-reports/worker-has-started-shift/worker-has-started-shift.component';
import { WorkerNoShiftStartedComponent } from './pages/reports/worker-reports/worker-no-shift-started/worker-no-shift-started.component';
import { WorkerLastShiftStartedComponent } from './pages/reports/worker-reports/worker-last-shift-started/worker-last-shift-started.component';
import { WorkerNoLocationComponent } from './pages/reports/worker-reports/worker-no-location/worker-no-location.component';
import { WorkerReportsMenuComponent } from './pages/reports/worker-reports/worker-reports-menu/worker-reports-menu.component';
import { WorkerShortShiftComponent } from './pages/reports/worker-reports/worker-short-shift/worker-short-shift.component';
import { WorkerLocationSettingsComponent } from './pages/reports/worker-reports/worker-location-settings/worker-location-settings.component';
import { ShiftuploadComponent } from './pages/reports/shiftupload/shiftupload.component';
import { WorkerAlertLoopsComponent } from './pages/reports/worker-reports/worker-alert-loops/worker-alert-loops.component';
import { WorkerEscalationListComponent } from './pages/reports/worker-reports/worker-escalation-list/worker-escalation-list.component';
import { WorkerDailyGroupCountComponent } from './pages/reports/worker-reports/worker-daily-group-count/worker-daily-group-count.component';
import { StartEndShiftsComponent } from './pages/reports/worker-reports/start-end-shifts/start-end-shifts.component';
import { WorkerMandownComponent } from './pages/reports/worker-reports/worker-mandown/worker-mandown.component';
import { WorkerFalldetectionComponent } from './pages/reports/worker-reports/worker-falldetection/worker-falldetection.component';
import { WorkerEndShiftComponent } from './pages/reports/worker-reports/worker-end-shift/worker-end-shift.component';
import { LastSyncComponent } from './pages/reports/worker-reports/last-sync/last-sync.component';
import { AppConnectivityComponent } from './pages/reports/worker-reports/app-connectivity/app-connectivity.component';
import { PermissionsComponent } from './pages/reports/worker-reports/permissions/permissions.component';
import { MonitorAssignmentSummaryComponent } from './pages/reports/worker-reports/monitor-assignment-summary/monitor-assignment-summary.component';

import { ReportsSchedulingComponent } from './pages/reports/reports-scheduling/reports-scheduling.component';

import { CancelledComponent } from './pages/account/cancelled/cancelled.component';
import { ExpiredComponent } from './pages/account/expired/expired.component';
import { AccountComponent } from './pages/account/account/account.component';
import { PlanComponent } from './pages/account/plan/plan.component';
import { Plan2024Component } from './pages/account/plan2024/plan2024.component';
import { SignupComponent } from './pages/account/signup/signup.component';

import { WorkerplanComponent } from './pages/settings/features/workerplan/workerplan.component';
import { Pricing2024Component } from './pages/settings/features/pricing2024/pricing2024.component';



import { TrialComponent } from './pages/account/trial/trial.component';
import { CardComponent } from './pages/account/card/card.component';
import { IncludedComponent } from './pages/account/included/included.component';
import { ExtendComponent } from './pages/account/extend/extend.component';
import { ContactComponent } from './pages/account/contact/contact.component';

import { FeaturesComponent } from './pages/settings/features/features.component';
import { BetaComponent } from './pages/settings/beta/beta.component';
import { FeatureRequestComponent } from './pages/settings/feature-request/feature-request.component';

import { SafetyzonesComponent } from './pages/mgmt/safetyzones/safetyzones/safetyzones.component';
import { SafetyzonesEditComponent } from './pages/mgmt/safetyzones/safetyzones-edit/safetyzones-edit.component';
import { SafetyzonesWorkersComponent } from './pages/mgmt/safetyzones/safetyzones-workers/safetyzones-workers.component';

import { ThreatsComponent } from './pages/mgmt/threats/threats/threats.component';
import { ThreatsCreateComponent } from './pages/mgmt/threats/threats-create/threats-create.component';
import { ThreatsEditComponent } from './pages/mgmt/threats/threats-edit/threats-edit.component';
import { ThreatsNotificationsComponent } from './pages/mgmt/threats/threats-notifications/threats-notifications.component';

import { LearningCenterComponent } from './pages/mgmt/learn/learning-center/learning-center.component';

import { SendMessageComponent } from './pages/mgmt/send-message/send-message.component';
import { BroadcastsListComponent } from './pages/mgmt/broadcasts/broadcasts-list/broadcasts-list.component';
import { BroadcastsCreateComponent } from './pages/mgmt/broadcasts/broadcasts-create/broadcasts-create.component';
import { BroadcastsDetailComponent } from './pages/mgmt/broadcasts/broadcasts-detail/broadcasts-detail.component';
import { AppLinksComponent } from './pages/mgmt/app-links/app-links.component';

import { ActivitiesComponent } from './pages/reports/activities/activities.component';
import { MonitoringValidationComponent } from './pages/mgmt/monitoring/monitoring-validation/monitoring-validation.component';
import { MonitoringComponent } from './pages/mgmt/monitoring/monitoring/monitoring.component';
import { MonitoringHomeComponent } from './pages/mgmt/monitoring/monitoring-home/monitoring-home.component';
import { MonitorAccessControlsComponent } from './pages/mgmt/monitors/monitor-access-controls/monitor-access-controls.component';
import { MonitoringSchedulingComponent } from './pages/mgmt/monitoring/monitoring-scheduling/monitoring-scheduling.component';

import { HomeComponent } from './pages/home/home.component';
import { ForgottenpasswordComponent } from './pages/auth/forgottenpassword/forgottenpassword.component';
import { PasswordresetComponent } from './pages/auth/passwordreset/passwordreset.component';
import { ServiceWorkerModule } from '@angular/service-worker';

import { LazyLoadService } from './services/lazy-load.service'; 
import { LazyStaticMapDirective } from './services/lazy-static-map.directive';

import { AngularSignaturePadModule } from '@almothafar/angular-signature-pad';

const APP_CONTAINERS = [
  DefaultAsideComponent,
  DefaultFooterComponent,
  DefaultHeaderComponent,
  DefaultLayoutComponent,
  EmailLayoutComponent
];

@NgModule({
  declarations: [AppComponent, ...APP_CONTAINERS,


  YesNoPipe,
  FieldtitlePipe,
  Nl2brPipe,
  UtcToTimezonePipe,

  WorkerReportsComponent,
  WorkerStatsComponent,
  WorkerStatusComponent,
  WorkerAppNotActivatedComponent,
  WorkerHasStartedShiftComponent,
  WorkerNoShiftStartedComponent,
  WorkerLastShiftStartedComponent,
  WorkerNoLocationComponent,
  WorkerReportsMenuComponent,
  WorkerShortShiftComponent,
  WorkerAlertLoopsComponent,
  WorkerEscalationListComponent,
  WorkerDailyGroupCountComponent,
  StartEndShiftsComponent,
  WorkerLocationSettingsComponent,
  WorkerMandownComponent,
  WorkerFalldetectionComponent,
  WorkerEndShiftComponent,
  WorkersInactiveComponent,

  ReportsSchedulingComponent,
  FeatureRequestComponent,

  SafetyzonesComponent,
  SafetyzonesEditComponent,
  SafetyzonesWorkersComponent,

  ThreatsComponent,
  ThreatsCreateComponent,
  ThreatsEditComponent,
  ThreatsNotificationsComponent,

  LearningCenterComponent,

  SendMessageComponent,
  BroadcastsListComponent,
  BroadcastsCreateComponent,
  BroadcastsDetailComponent,
  AppLinksComponent,
  WorkerIhelpComponent,

  YesNoPipe,
  FieldtitlePipe,
  CancelledComponent,
  ExpiredComponent,
  FeaturesComponent,

  BetaComponent,
  PlanComponent,
  Plan2024Component,
  TrialComponent,
  CardComponent,
  IncludedComponent,
  ExtendComponent,
  ContactComponent,
  ChooseAppComponent,
  WorkersUploadComponent,
  HomeComponent,
  ForgottenpasswordComponent,
  PasswordresetComponent,
  SettingsComponent,
  WorkersComponent,
  WorkerAddComponent,
  WorkerEditComponent,
  WorkersMassdeleteComponent,
  MonitorsComponent,
  MonitorsPrivacyComponent,
  MonitorPrivacyEditComponent,
  MonitorAddComponent,
  MonitorEditComponent,
  MonitorsUploadComponent,
  VehiclesComponent,
  DevicesComponent,
  EscalationComponent,
  EscalationEditCustomComponent,
  EscalationEditWizardComponent,
  ShiftsComponent,
  ShiftComponent,
  AlertsComponent,
  AlertPageComponent,
  ActionsComponent,
  LocationsComponent,
  OffenderListComponent,
  WeatherAlertsComponent,
  TimesheetsComponent,
  AccountComponent,
  ProfileComponent,
  HelpComponent,
  WorkersImportComponent,
  MonitorsImportComponent,
  AlertloopsComponent,
  AlertloopEditComponent,
  AlertloopsUploadComponent,
  GroupsComponent,
  GroupEditComponent,
  GroupsUploadComponent,
  OutofhoursComponent,
  StartEndComponent,
  W3wComponent,
  ZapComponent,
  LiveMonitorComponent,
  AwardsHomeComponent,
  AwardsNavComponent,
  AwardsMainComponent,
  AwardsExamplesComponent,
  AwardsFaqComponent,
  ChampionsComponent,
  CertificatesComponent,
  AwardsDemoComponent,
  ReportcardsComponent,
  CompanyrankingsComponent,
  WorkerrankingsComponent,
  WorkerInfoComponent,
  WorkerCheckinComponent,
  WorkerSafetyzoneComponent,
  WorkerEscalationComponent,
  WorkerShiftComponent,
  WorkerAppComponent,
  WorkerPermissionsComponent,
  WorkerSpotComponent,
  MessagesComponent,
  GroupEscalationComponent,
  WorkerEscalationComponent,
  WorkersAdpComponent,
  MonitorsAdpComponent,
  ImageUploadComponent,
  ShiftuploadComponent,
  WorkerZoleoComponent,
  ActivitiesComponent,
  WorkerplanComponent,
  Pricing2024Component,
  MonitoringSchedulingComponent,
  MonitoringValidationComponent,
  MonitoringHomeComponent,
  MonitoringComponent,
  LastSyncComponent,
  MonitorAccessControlsComponent,
  AppConnectivityComponent,
  PermissionsComponent,
  MonitorAssignmentSummaryComponent,
  SignupComponent,
  LazyStaticMapDirective 
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    AvatarModule,
    BreadcrumbModule,
    FooterModule,
    DropdownModule,
    GridModule,
    HeaderModule,
    SidebarModule,
    IconModule,
    NavModule,
    ButtonModule,
    FormModule,
    UtilitiesModule,
    ButtonGroupModule,
    ReactiveFormsModule,
    SidebarModule,
    SharedModule,
    TabsModule,
    ListGroupModule,
    ProgressModule,
    BadgeModule,
    ListGroupModule,
    CardModule,
    NgScrollbarModule,
    HttpClientModule,
    TranslocoRootModule,
    AngularSignaturePadModule,

    ColorPickerModule,
    NgSelectModule,
    FormsModule,
    CountdownModule,
    NgxDaterangepickerMd.forRoot(),
    FileUploadModule,
    NgxSliderModule,
    FullCalendarModule,

    TableModule,
    ModalModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),

  ],
  providers: [
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy
    },
    IconSetService,
    Title,
    LazyLoadService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
